/**
 * @file The single presentation-boundary vocabulary for the Mapping Review UI.
 * The pipeline's internal tokens (`junk`, `chunk`, `fact`, reference "slots",
 * the casing-as-meaning rule kinds) are accurate in code but leak as judgmental
 * or undecodable jargon when shown verbatim — on the highest-stakes,
 * write-to-system copy. Every surface renders through this map instead, so the
 * user sees one consistent human vocabulary (Data/Subtotal/Noise, a single
 * Skip/Keep verb pair, "section", "extracted value", "Linked (manual)" /
 * "Plain text") while the code keeps the raw tokens. Stored enums stay keyed by
 * token; this module owns the displayed strings and the shared token→AntD-status
 * `tagColor` map (the one place a row class picks its colour).
 */
import type { CellCleaningRule, RowClassificationTag } from '#types';

const rowClass: Record<RowClassificationTag, string> = {
  value: 'Data',
  summary: 'Subtotal',
  junk: 'Noise',
};

const tagColor: Record<RowClassificationTag, string> = {
  value: 'green',
  summary: 'orange',
  junk: 'red',
};

const rule: Record<CellCleaningRule['kind'], string> = {
  trim: 'Trim whitespace',
  lowercase: 'Lowercase',
  uppercase: 'Uppercase',
  replace: 'Find & replace',
  coerceNumber: 'Convert to number',
  coerceDate: 'Convert to date',
};

const plural = (n: number, noun: string): string => `${n} ${noun}${n === 1 ? '' : 's'}`;

export const mappingReviewLabels = {
  /** Row classification token → user-facing class name. */
  rowClass,
  /** Row classification token → AntD status colour for its Tag. */
  tagColor,
  /** Cleaning-rule kind → sentence-case verb for the rules menu/tags. */
  rule,

  /** One keep/drop verb pair, used at every altitude (row, column, table). */
  skip: 'Skip',
  keep: 'Keep',
  restore: 'Restore',
  /** Placeholder for an unmapped column (replaces "will be dropped"). */
  notMapped: 'Not mapped — will be skipped',

  /** Source-granularity wording (replaces "chunk"). */
  section: 'section',
  allSections: 'All sections (combined)',

  /** Extracted-value wording (replaces "fact"). */
  extractedValue: 'extracted value',
  allValues: 'All values',
  rePickValue: 'Re-pick value',
  loadingFields: 'Loading fields…',
  aiSuggestion: 'AI suggestion',

  /** Reference-resolution states. */
  linkedManual: 'Linked (manual)',
  plainText: 'Plain text',
  setAllPlainText: 'Set all to plain text',
  noMatch: 'No match',
  weakGuess: (label: string): string => `Weak guess ${label}`,

  /** Footer "not yet linked" counts (replaces "unresolved … slot/cell reference"). */
  unresolvedReferenceFields: (n: number): string => `${plural(n, 'reference field')} not yet linked`,
  unresolvedColumnValues: (n: number): string => `${plural(n, 'column value')} not yet linked`,
} as const;
